import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Newspaper } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { LangToggle, useLang } from "@/lib/i18n";
import api, { API } from "@/lib/api";

export default function InsightsPage() {
  const { t } = useLang();
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/content/insights")
      .then(({ data }) => setPosts(data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <div data-testid="insights-page" className="min-h-screen bg-midnight">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center justify-between mb-16">
          <Link to="/" data-testid="insights-back-home" className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-gold transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" /> NOREX
          </Link>
          <LangToggle compact />
        </div>

        <Reveal>
          <Eyebrow>{t.insights.eyebrow}</Eyebrow>
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <h1 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F4F6F8] max-w-xl" data-testid="insights-title">
              {t.insights.title}
            </h1>
            <p className="text-base text-[#94A3B8] max-w-sm">{t.insights.sub}</p>
          </div>
        </Reveal>

        {loaded && posts.length === 0 ? (
          <div data-testid="insights-empty" className="border border-dashed border-[#1E293B] py-24 flex flex-col items-center gap-3 text-[#94A3B8]">
            <Newspaper className="w-8 h-8" />
            <p className="text-sm">{t.insights.empty}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.07}>
                <Link
                  to={`/insights/${p.id}`}
                  data-testid={`insight-card-${p.id}`}
                  className="group block h-full bg-surface border border-[#1E293B] overflow-hidden hover:border-gold/50 hover:-translate-y-1 transition-[border-color,transform] duration-300"
                >
                  {p.cover && (
                    <div className="h-44 overflow-hidden">
                      <img src={`${API}/public/files/${p.cover.storage_path}`} alt={p.title}
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    </div>
                  )}
                  <div className="p-7">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-3">
                      {p.category} · {new Date(p.created_at).toLocaleDateString()}
                    </p>
                    <h2 className="text-lg font-semibold text-[#F4F6F8] mb-3 leading-snug">{p.title}</h2>
                    <p className="text-sm text-[#94A3B8] leading-relaxed mb-5">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-2 text-sm text-gold">
                      {t.insights.readMore}
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
